import { LoadingOutlined } from '@ant-design/icons'
import { Bubble } from '@ant-design/x'
import { App, ChatMessage, OrganizationMember } from '@prisma/client'
import { Flex, Spin } from 'antd'
import React, { useEffect, useRef } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { styled } from 'styled-components'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { FeatureAppClientShared } from '~/features/app/clientShared'
import { FeatureAppSessionClientShared } from '~/features/appSession/clientShared'
import { ChatSocketPayload } from '~/features/chatV3/common'
import { OrganizationType } from '~/features/organization/common'
import { SocketClient } from '~/plugins/socket/client'
import { Toolbox } from '~/plugins/toolbox/shared'
import { ErrorAlert } from './components/ErrorAlert'
import { LiveActions } from './components/LiveActions'
import { SendMessageInput } from './components/SendMessageInput'
import { ThreadPlaceholder } from './components/ThreadPlaceholder'
import { useBubbles } from './hooks/useBubbles'
import { useConversationParticipants } from './hooks/useConversationParticipants'
import { useFatalError } from './hooks/useFatalError'
import { useMessagesV2 } from './hooks/useMessagesV2'
import { useOtherParticipants } from './hooks/useOtherParticipants'
import { useParticipantsTyping } from './hooks/useParticipantsTyping'
import { useSendDraft } from './hooks/useSendDraft'
import { useTrackMessageRead } from './hooks/useTrackMessageRead'

const WrapperMessage = styled(Bubble)<{ $itemIndex: number }>`
  position: relative;
  padding: 8px 16px 8px 16px;

  ${props => {
    const isFirst = props.$itemIndex === 0

    if (isFirst) {
      return `
        margin-bottom: 30px;
      `
    } else {
      return ``
    }
  }}

  .floating-toolbar {
    position: absolute;
    transform: translateY(calc(-8px - 50%));
    right: 20px;
    top: 0;
    display: none;
    z-index: 1;
  }

  &:hover {
    // background: #fafafa;

    .floating-toolbar {
      display: flex;
    }
  }
`

type Props = {
  conversationId: string
  messageParent?: ChatMessage
  organization: OrganizationType
  organizationMember: OrganizationMember
  organizationMembers: OrganizationMemberFromContext[]
  onRefreshOrganizationMembers: () => void
  onOpenThread?: (message: ChatMessage) => void
  canSendMessage?: boolean
}

export const ChatThread: React.FC<Props> = ({
  conversationId,
  messageParent,
  organization,
  organizationMember,
  organizationMembers,
  onRefreshOrganizationMembers,
  onOpenThread,
  canSendMessage = true,
}) => {
  const refList = useRef<HTMLElement>(null)
  const refEndOfFeed = useRef<HTMLElement>(null)

  const {
    participants,
    organizationContacts,
    isReady: isReadyParticipants,
    participantUser,
    refetch: refetchParticipants,
  } = useConversationParticipants({
    conversationId,
    organizationMemberUserId: organizationMember.id,
  })

  const {
    isReady,
    hook,
    onMessageSent,
    onMessageSentOptimist,
    messages,
    ...messagesLogic
  } = useMessagesV2({
    refList,
    containerId: `thread-messages-container${messageParent?.id ?? ''}`,
    conversationId,
    messageParent: messageParent,
    participantUser,
  })

  const draftLogic = useSendDraft({ organizationMember, onSent: () => {} })

  useTrackMessageRead({
    conversationId,
    participant: participants.find(
      item => item.organizationMemberId === organizationMember.id,
    ),
    messageLast: hook.items[0],
  })

  const { members } = useOtherParticipants({
    organizationMember,
    participants,
    organizationMembers,
  })

  const member = members.length === 1 ? members[0] : null
  const appSession = member?.organizationMember?.appSession
  const app = appSession?.app

  const settings = FeatureAppClientShared.Helper.getUISettings(app as App)
  const canUploadAttachments = settings?.chat?.canUploadAttachments
  const typeAttachments = settings?.chat?.typeAttachments
  const messageSuggestions = settings?.chat?.messageSuggestions

  const { items, roles } = useBubbles({
    onOpenThread,
    onSendDraft: draftLogic.send,
    onDelete: messagesLogic.deleteMessage,
    organization,
    organizationMembers,
    organizationContacts,
    organizationMember,
    messages: messages,
    participants,
  })

  const { typing, onTypeOptimistic } = useParticipantsTyping({
    conversationIds: [conversationId],
    organization,
    organizationMembers,
    organizationMemberAgent: member?.organizationMember,
  })

  const fatalError = useFatalError({
    appSession,
    organization,
    organizationMemberAgent: member?.organizationMember,
  })

  SocketClient.useEvent<ChatSocketPayload>(
    `organization-${organization.id}-chat`,
    async payload => {
      if (conversationId !== payload.conversationId) {
        return
      }

      if (payload.type === 'new-reaction') {
        hook.refetch()
        return
      }

      if (payload.type === 'new-message') {
        await hook.refetch()
        await refetchParticipants()

        await Toolbox.Utility.sleep(500)

        refEndOfFeed.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }

      onRefreshOrganizationMembers()
    },
  )

  const onboarding = FeatureAppSessionClientShared.useOnboarding({ appSession })

  const handleMessageSent = (message: ChatMessage) => {
    onMessageSent(message)
  }

  const handleMessageSentOptimist = (message: ChatMessage) => {
    onMessageSentOptimist(message)
    onTypeOptimistic(message, member?.organizationMember)
  }

  const hasVerticalScrollbar = (element: HTMLElement): boolean => {
    if (!element) {
      return false
    }
    return element.scrollHeight > element.clientHeight
  }

  useEffect(() => {
    if (hook.items.length > 0) {
      const shouldFetch =
        !hasVerticalScrollbar(refList.current) &&
        hook.hasNextPage &&
        !hook.isFetching

      if (shouldFetch) {
        hook.fetchNextPage()
      }
    }
  }, [hook.items.length])

  return (
    <>
      <Flex
        vertical
        className="w-full h-full pb-4"
        align="center"
        style={{ overflowY: 'hidden' }}
      >
        <Flex
          className="w-full"
          vertical
          flex={1}
          style={{
            overflowY: 'hidden',
          }}
        >
          <Flex
            id={hook.containerId}
            ref={refList}
            vertical
            style={{
              overflowY: 'auto',
              flexDirection: 'column-reverse',
            }}
          >
            <InfiniteScroll
              dataLength={items.length}
              // next={null}
              next={() => {
                if (!hook.isFetching) {
                  hook.fetchNextPage()
                }
              }}
              hasMore={hook.hasNextPage}
              // loader={null}
              loader={
                <Spin
                  indicator={<LoadingOutlined spin />}
                  style={{ margin: '10px auto' }}
                />
              }
              style={{
                display: 'flex',
                flexDirection: 'column-reverse',
                overflow: 'visible',
              }}
              scrollableTarget={hook.containerId}
              inverse={true}
            >
              <span ref={refEndOfFeed} />

              {onboarding.isPending &&
                appSession?.status !== 'DISABLED' &&
                items?.length > 0 && (
                  <FeatureAppSessionClientShared.OnboardingButton
                    appSession={appSession}
                    app={appSession.app as App}
                    onSubmit={onRefreshOrganizationMembers}
                  />
                )}

              {fatalError.isVisible && (
                <>
                  <ErrorAlert onClose={fatalError.close} />
                </>
              )}

              {items.map((item, itemIndex) => {
                const role = roles[item.role]
                const message = item.content['item']
                const specialType = [
                  'date-splitter',
                  'agent-disabled',
                  'agent-limit-reached',
                ]

                if (specialType.includes(item.role)) {
                  return (
                    <Flex key={message.id}>
                      {role.messageRender(item.content as any)}
                    </Flex>
                  )
                }

                return (
                  <WrapperMessage
                    key={message.id}
                    placement="start"
                    messageRender={() =>
                      role.messageRender(item.content as any)
                    }
                    $itemIndex={itemIndex}
                    avatar={item.avatar}
                    variant="borderless"
                    style={{ width: '100%' }}
                    styles={{ content: { width: '100%' } }}
                  />
                )
              })}

              {!messageParent &&
                items.length === 0 &&
                isReady &&
                isReadyParticipants && (
                  <>
                    <ThreadPlaceholder
                      organization={organization}
                      organizationMember={organizationMember}
                      participants={participants}
                      organizationMembers={organizationMembers}
                    />
                  </>
                )}
            </InfiniteScroll>
          </Flex>
        </Flex>

        <LiveActions
          conversationId={conversationId}
          messageParent={messageParent}
          typing={typing}
        />

        {canSendMessage && (
          <SendMessageInput
            isUploadAttachments={canUploadAttachments}
            typeAttachments={typeAttachments}
            appSession={appSession}
            suggestions={messageSuggestions}
            conversationId={conversationId}
            messageParent={messageParent}
            organization={organization}
            organizationMember={organizationMember}
            typing={typing}
            onSent={handleMessageSent}
            onSentOptimist={handleMessageSentOptimist}
          />
        )}
      </Flex>
    </>
  )
}
